import { Box, Button, Center, Group, Modal, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserResponse } from '@/api/generated/models';
import { MutationButton } from '@/components/common';

type Role = 'USER' | 'ORGANISATION_ADMIN';

interface ChangeRoleModalProps {
  user: UserResponse | null;
  onClose: () => void;
  onConfirm: (userId: string, role: Role) => void;
  loading?: boolean;
  isPaused?: boolean;
}

const initials = (firstName?: string, lastName?: string) =>
  `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase();

const RadioDot = ({ selected }: { selected: boolean }) => (
  <Center
    w={16}
    h={16}
    style={{
      borderRadius: 999,
      border: `1.5px solid ${selected ? 'var(--color-accent-primary)' : 'var(--color-border-default)'}`,
      backgroundColor: 'var(--color-bg-surface)',
      flexShrink: 0
    }}
  >
    {selected && (
      <Box
        w={8}
        h={8}
        style={{ borderRadius: 999, backgroundColor: 'var(--color-accent-primary)' }}
      />
    )}
  </Center>
);

export const ChangeRoleModal = ({
  user,
  onClose,
  onConfirm,
  loading,
  isPaused
}: ChangeRoleModalProps) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<Role>('USER');

  useEffect(() => {
    if (user) setSelectedRole(user.role === 'ORGANISATION_ADMIN' ? 'ORGANISATION_ADMIN' : 'USER');
  }, [user]);

  const isAdmin = user?.role === 'ORGANISATION_ADMIN' || user?.role === 'APP_ADMIN';

  return (
    <Modal
      opened={!!user}
      onClose={onClose}
      centered
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.55 }}
      title={t('users.roles.changeRoleTitle')}
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)',
          padding: '28px 28px 20px'
        },
        title: { fontSize: 19, fontWeight: 700, color: 'var(--color-text-primary)' },
        body: { padding: '20px 28px 28px' }
      }}
    >
      {user && (
        <Stack gap={20}>
          <Group gap={10} wrap="nowrap">
            <Center
              w={32}
              h={32}
              style={{
                borderRadius: 999,
                backgroundColor: 'var(--color-border-soft)',
                border: '1px solid var(--color-border-default)',
                flexShrink: 0
              }}
            >
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {initials(user.firstName, user.lastName)}
              </Text>
            </Center>
            <Stack gap={0}>
              <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {user.firstName} {user.lastName}
              </Text>
              <Text fz={10.5} style={{ color: 'var(--color-text-muted)' }}>
                {user.email}
              </Text>
            </Stack>
          </Group>

          <Box style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />

          <Stack gap={6}>
            <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
              {t('users.roles.currentRole')}
            </Text>
            <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
              {isAdmin ? t('users.invite.roleAdmin') : t('users.invite.roleUser')}
            </Text>
          </Stack>

          <Stack gap={12}>
            <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
              {t('users.roles.newRole')}
            </Text>
            <Stack gap={12}>
              {(['USER', 'ORGANISATION_ADMIN'] as const).map((role) => {
                const selected = selectedRole === role;
                return (
                  <UnstyledButton
                    key={role}
                    onClick={() => setSelectedRole(role)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <RadioDot selected={selected} />
                    <Text
                      fz={13.5}
                      fw={selected ? 700 : 500}
                      style={{
                        color: selected
                          ? 'var(--color-text-primary)'
                          : 'var(--color-text-secondary)'
                      }}
                    >
                      {role === 'ORGANISATION_ADMIN'
                        ? t('users.invite.roleAdmin')
                        : t('users.invite.roleUser')}
                    </Text>
                  </UnstyledButton>
                );
              })}
            </Stack>
          </Stack>

          <Group gap={10} wrap="nowrap">
            <Button variant="outline" fullWidth onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              fullWidth
              isPending={loading}
              isPaused={isPaused}
              disabled={selectedRole === user.role}
              onClick={() => user.id && onConfirm(user.id, selectedRole)}
              style={
                selectedRole === user.role
                  ? { backgroundColor: 'var(--color-accent-primary-hover)', opacity: 1 }
                  : undefined
              }
            >
              {t('users.roles.saveChange')}
            </MutationButton>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
